import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import supermanImg from '../images/superman.webp';

const SupermanBackToTop = () => {
  const [direction, setDirection] = useState('up');
  const [isLaunching, setIsLaunching] = useState(false);
  const [smokeParticles, setSmokeParticles] = useState([]);

  // Track previous scroll position with useRef
  const prevScrollY = useRef(0);

  // Fixed Superman animation based on scroll direction
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Set direction based on comparison with previous position
      if (currentScrollY > prevScrollY.current) {
        setDirection('down');
      } else {
        setDirection('up');
      }

      // Update ref
      prevScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Rocket launch handler
  const handleRocketLaunch = () => {
    if (isLaunching) return; // Prevent multiple clicks during animation

    setIsLaunching(true);

    // Generate smoke particles with lighter appearance
    const particles = [];
    for (let i = 0; i < 25; i++) {
      particles.push({
        id: Math.random(),
        delay: i * 0.05,
        x: (Math.random() - 0.5) * 45,
        scale: 0.5 + Math.random() * 0.9
      });
    }
    setSmokeParticles(particles);

    // Scroll to top with smooth animation
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Reset after animation completes (longer duration for slower animation)
    setTimeout(() => {
      setIsLaunching(false);
      setSmokeParticles([]);
    }, 3000);
  };

  return (
    <>
      {/* Superman Rocket Launch - Back to Top Button */}
      <AnimatePresence>
        {!isLaunching && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, y: -window.innerHeight, scale: 0.3 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            style={{
              position: 'fixed',
              bottom: '30px',
              right: '20px',
              zIndex: 100,
              cursor: 'pointer'
            }}
            onClick={handleRocketLaunch}
          >
            <motion.img
              src={supermanImg}
              alt="Back to Top"
              loading="lazy"
              className={`superman-scroll ${direction}`}
              style={{
                width: '50px',
                height: 'auto',
                filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
                transform: direction === 'up'
                  ? 'rotate(-10deg)'
                  : 'rotate(10deg)'
              }}
              whileHover={{ scale: 1.2, rotate: -5 }}
              whileTap={{ scale: 0.9 }}
            />
          </motion.div>
        )}

        {isLaunching && (
          <motion.div
            initial={{ opacity: 1, bottom: 30 }}
            animate={{
              opacity: 0,
              bottom: window.innerHeight + 100,
              scale: [1, 1.4, 1.2, 0.4],
              rotate: [-10, -15, -25, -35]
            }}
            transition={{
              duration: 2.8,
              ease: [0.6, 0.05, 0.01, 0.95],
              times: [0, 0.3, 0.7, 1]
            }}
            style={{
              position: 'fixed',
              right: '20px',
              zIndex: 100,
              pointerEvents: 'none'
            }}
          >
            <img
              src={supermanImg}
              alt="Launching"
              loading="lazy"
              style={{
                width: '50px',
                height: 'auto',
                filter: 'drop-shadow(0 8px 20px rgba(59, 130, 246, 0.8)) drop-shadow(0 0 30px rgba(147, 51, 234, 0.6))'
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Smoke/Fog Particles */}
      <AnimatePresence>
        {smokeParticles.map((particle) => (
          <motion.div
            key={particle.id}
            initial={{
              opacity: 0.7,
              scale: particle.scale,
              bottom: 30
            }}
            animate={{
              opacity: 0,
              scale: particle.scale * 2.8,
              bottom: 180,
              x: particle.x
            }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 1.5,
              delay: particle.delay,
              ease: "easeOut"
            }}
            style={{
              position: 'fixed',
              right: '40px',
              width: '35px',
              height: '35px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(156, 163, 175, 0.7) 0%, rgba(107, 114, 128, 0.4) 50%, transparent 100%)',
              pointerEvents: 'none',
              zIndex: 99,
              filter: 'blur(10px)'
            }}
          />
        ))}
      </AnimatePresence>

      {/* Enhanced CSS for Superman Rocket Launch */}
      <style jsx>{`
        @keyframes fly-up {
          0% { transform: translateY(0) rotate(-10deg); }
          50% { transform: translateY(-8px) rotate(-15deg); }
          100% { transform: translateY(0) rotate(-10deg); }
        }

        @keyframes fly-down {
          0% { transform: translateY(0) rotate(10deg); }
          50% { transform: translateY(8px) rotate(15deg); }
          100% { transform: translateY(0) rotate(10deg); }
        }

        @keyframes rocket-glow {
          0%, 100% {
            filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))
                    drop-shadow(0 0 10px rgba(59, 130, 246, 0.3));
          }
          50% {
            filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))
                    drop-shadow(0 0 25px rgba(59, 130, 246, 0.8))
                    drop-shadow(0 0 40px rgba(147, 51, 234, 0.5));
          }
        }

        .superman-scroll.up {
          animation: fly-up 2s ease-in-out infinite;
        }

        .superman-scroll.down {
          animation: fly-down 2s ease-in-out infinite;
        }

        .superman-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </>
  );
};

export default SupermanBackToTop;
